import { useCallback, useRef, useState } from 'react';

import { Profession } from 'app/services/oabEligibility';
import {
  OabCaptureSurface,
  trackOabCaptureError,
  trackOabCaptureLawyerAnswered,
} from 'app/services/tracking/oabCapture/oabCaptureEvents';

export const OAB_CAPTURE_ANSWER_ERROR =
  'Não foi possível salvar sua resposta. Tente novamente.';

type UseOabCaptureAnswerParams = {
  surface: OabCaptureSurface;
  answerProfession: (profession: Profession) => Promise<unknown>;
  onAnswered: (isLawyer: boolean) => void;
};

type UseOabCaptureAnswer = {
  answer: (isLawyer: boolean) => Promise<void>;
  hasAnswerFailed: boolean;
  isAnswering: () => boolean;
};

export function useOabCaptureAnswer({
  surface,
  answerProfession,
  onAnswered,
}: UseOabCaptureAnswerParams): UseOabCaptureAnswer {
  const [hasAnswerFailed, setHasAnswerFailed] = useState(false);
  // A flag de carregamento da mutação não muda no mesmo tick do segundo clique,
  // então a guarda de duplo clique tem de ser por referência.
  const isAnsweringRef = useRef(false);

  const answer = useCallback(
    async (isLawyer: boolean): Promise<void> => {
      if (isAnsweringRef.current) {
        return;
      }

      isAnsweringRef.current = true;
      setHasAnswerFailed(false);

      const profession: Profession = isLawyer ? 'LAWYER' : 'OTHER';

      try {
        await answerProfession(profession);
      } catch (error) {
        setHasAnswerFailed(true);
        trackOabCaptureError({
          surface,
          step: 'question',
          errorAction: 'profession_answer',
          error,
          profession,
        });
        return;
      } finally {
        isAnsweringRef.current = false;
      }

      trackOabCaptureLawyerAnswered({ surface, profession });
      // A resposta decide para onde a superfície vai (formulário ou
      // agradecimento), então quem responde recebe qual foi.
      onAnswered(isLawyer);
    },
    [answerProfession, surface, onAnswered],
  );

  // Read through the same ref the double-click guard uses, so a surface can lock
  // its dismiss in the click that follows without waiting for a re-render.
  const isAnswering = useCallback((): boolean => isAnsweringRef.current, []);

  return { answer, hasAnswerFailed, isAnswering };
}
